"use client";

import Link, { LinkProps } from "next/link";
import { usePathname } from "next/navigation";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import {
  getLocaleFromPath,
  addLocaleToPath,
  isLocaleExcludedPath,
  isValidLocale,
  type SupportedLocale,
} from "@/app/lib/i18n-config";

interface LocalizedLinkProps extends Omit<LinkProps, "href"> {
  href: string;
  locale?: SupportedLocale;
  children: React.ReactNode;
  className?: string;
  target?: string;
  rel?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  title?: string;
  style?: React.CSSProperties;
  "aria-label"?: string;
  "aria-current"?:
    | "page"
    | "step"
    | "location"
    | "date"
    | "time"
    | "true"
    | "false";
}

/**
 * A localized version of next/link that automatically prefixes paths with the current locale.
 *
 * - Reads current locale from pathname (or from i18n on /blog and /job, which have no prefix)
 * - Automatically adds locale prefix for non-German locales
 * - Supports explicit locale override via `locale` prop
 * - External URLs (starting with http/https or //) are passed through unchanged
 * - Anchor links (starting with #) are passed through unchanged
 * - /blog and /job paths are excluded from locale prefixing (content is single-language)
 */
const LocalizedLink = forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
  function LocalizedLink(
    { href, locale: explicitLocale, children, ...props },
    ref
  ) {
    const { i18n } = useTranslation();
    const pathname = usePathname();
    // /blog and /job URLs carry no locale prefix; their language comes from the
    // content (set in i18n), so links there must follow it instead of defaulting to German
    const contentLocale = i18n?.language;
    const currentLocale =
      pathname && isLocaleExcludedPath(pathname) && contentLocale && isValidLocale(contentLocale)
        ? contentLocale
        : getLocaleFromPath(pathname ?? "");

    // Determine which locale to use
    const targetLocale = explicitLocale ?? currentLocale;

    // Check if href is external, anchor, or special path
    const isExternal =
      href.startsWith("http://") ||
      href.startsWith("https://") ||
      href.startsWith("//");
    const isAnchor = href.startsWith("#");
    const isMailto = href.startsWith("mailto:");
    const isTel = href.startsWith("tel:");

    // Pass through external URLs, anchors, and special protocols unchanged
    if (isExternal || isAnchor || isMailto || isTel) {
      return (
        <Link ref={ref} href={href} {...props}>
          {children}
        </Link>
      );
    }

    // Check if path should be excluded from localization (blog, job)
    if (isLocaleExcludedPath(href)) {
      return (
        <Link ref={ref} href={href} {...props}>
          {children}
        </Link>
      );
    }

    // Add locale prefix to internal paths
    const localizedHref = addLocaleToPath(href, targetLocale);

    return (
      <Link ref={ref} href={localizedHref} {...props}>
        {children}
      </Link>
    );
  }
);

export default LocalizedLink;
